import { OffersReminderToggle } from '@/components/hotels/OffersReminderToggle';
import { OfferMultiSelectDialog } from '@/components/promotional-offers/OfferMultiSelectDialog';
import {
  promotionalOfferCrudConfig,
  type PromotionalOffer,
} from '@/config/objects/promotional-offers';
import {
  Button,
  CrudListView,
  toast,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const CANDIDATE_LIMIT = 200;

export function HotelOffersTab() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  // Offers already linked to this hotel — used both to hide them from the
  // "Add existing" picker and (as ids) to filter the candidate fetch.
  const { data: linkedData } = useQuery<{ data: PromotionalOffer[] }>({
    queryKey: ['HotelPromotionalOffers', id],
    enabled: addOpen && !!id,
    queryFn: () =>
      apiClient.get<{ data: PromotionalOffer[] }>(
        `/hotels/${id}/promotional-offers?limit=${CANDIDATE_LIMIT}`,
      ),
  });

  const { data: candidateData, isLoading: candidatesLoading } = useQuery<{
    data: PromotionalOffer[];
  }>({
    queryKey: ['HotelOffersTab', 'candidates', id],
    enabled: addOpen && !!id,
    queryFn: () =>
      apiClient.get<{ data: PromotionalOffer[] }>(
        `/promotional-offers?limit=${CANDIDATE_LIMIT}`,
      ),
  });

  const linkedOfferIds = useMemo(
    () => new Set((linkedData?.data ?? []).map((offer) => offer.id)),
    [linkedData?.data],
  );

  const handleAddMany = async (selected: PromotionalOffer[]) => {
    if (!id || selected.length === 0) return;
    const results = await Promise.allSettled(
      selected.map((offer) =>
        apiClient.post(`/hotels/${id}/promotional-offers`, {
          promotionalOfferId: offer.id,
        }),
      ),
    );

    const failed = results.filter((r) => r.status === 'rejected').length;
    const succeeded = selected.length - failed;

    if (succeeded > 0) {
      toast.success(`Added ${succeeded} ${succeeded === 1 ? 'offer' : 'offers'} to hotel`);
    }
    if (failed > 0) {
      toast.error(`Failed to add ${failed} ${failed === 1 ? 'offer' : 'offers'}`);
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['Promotional Offer', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['HotelPromotionalOffers', id] }),
      queryClient.invalidateQueries({ queryKey: ['HotelOffersTab', 'candidates', id] }),
    ]);

    setAddOpen(false);
  };

  // Hotel-scoped list: trimmed to a focused column set with no filter/search/sort
  // toolbar (matches the hotel Amenities tab). Built-in add/create are removed —
  // both flows live in the "Add offer" dropdown — but inline remove (unlink) and
  // view stay, and `baseUrl` keeps every navigation inside the hotel context.
  const hotelPromotionalOffersConfig: CrudConfig<PromotionalOffer> = useMemo(
    () => ({
      ...promotionalOfferCrudConfig,
      entityName: 'Promotional Offer',
      entityNamePlural: 'Promotional Offers',
      endpoints: {
        ...promotionalOfferCrudConfig.endpoints,
        list: `/hotels/${id}/promotional-offers`,
        delete: (promotionalOfferId: string) =>
          `/hotels/${id}/promotional-offers/${promotionalOfferId}`,
      },
      listView: {
        ...promotionalOfferCrudConfig.listView,
        // Richer hotel-facing columns (each key has a formatter in the base
        // config): thumbnail, names, the discount, status, active flag, expiry.
        fields: [
          'featuredImage',
          'name',
          'title',
          'discountType',
          'status',
          'isActive',
          'validUntil',
        ],
        searchable: false,
        searchableFields: [],
        sortableFields: [],
        filters: [],
        actions: {
          view: { enabled: true },
          remove: {
            enabled: true,
            onRemove: async (promotionalOffer: PromotionalOffer) => {
              await apiClient.delete(
                `/hotels/${id}/promotional-offers/${promotionalOffer.id}`,
              );
              toast.success('Promotional offer removed from hotel');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  if (!id) return null;

  const addActions = (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <Button variant="outline" onClick={() => setAddOpen(true)}>
        Add existing
      </Button>
      <Button variant="default" icon={Plus} onClick={() => navigate(`/hotels/${id}/offers/new`)}>
        Create new
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 p-0">
      <OffersReminderToggle hotelId={id} />

      <CrudListView
        config={hotelPromotionalOffersConfig}
        baseUrl={`/hotels/${id}/offers`}
        headerRight={addActions}
        onItemClick={(offer: PromotionalOffer) =>
          navigate(`/hotels/${id}/offers/${offer.id}`)
        }
        getValueFromItem={(offer: PromotionalOffer) => offer.id}
        getLabelFromItem={(offer: PromotionalOffer) =>
          offer.name || offer.title || 'Unnamed Offer'
        }
      />

      <OfferMultiSelectDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add offers to hotel"
        description="Pick existing offers to link to this hotel. Offers already linked are hidden."
        offers={candidateData?.data ?? []}
        isLoading={candidatesLoading}
        excludeIds={linkedOfferIds}
        onSubmit={handleAddMany}
        emptyMessage="No offers available to add — every offer is already linked to this hotel."
      />
    </div>
  );
}
